import { register } from "@rill/loader";
import orderBy from "lodash/orderBy";
import appendTypeAndCountry from "../utils/append-type-and-country";
import {
  calculateFreeTrialDaysLeft,
  getCoachStripePlan,
  getCoachTrialStatus,
} from "../utils/user-helpers";

register(
  {
    name: "athlete",
    // "exercise" is included because this loader's exerciseLibrary/
    // repMaximums fields are bumped via updateLastModifiedCoach's
    // coachName param (["exercise"], not "user") when only the athlete's
    // library changed, not the coach's own document.
    meta: ["no-bust", "user", "exercise"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: [
            "name",
            "email",
            "avatar",
            "registered",
            "exerciseLibrary",
            "repMaximums",
            "preferredLoadInput",
            "percent",
            "units",
            "country",
            "admin",
            "permissions",
            "ambassador",
            "stripe",
            "primaryExercises",
            "tasks",
            "goals",
            "equipment",
            "limitations",
            "round",
            "coach._id",
            "coach.organizationId",
            "coach.name",
            "accountability",
          ],
        },
      });
      const data = await res.json();
      const { actualUser = {} } = data;
      if (actualUser.accountability) {
        actualUser.customAccountability = actualUser.accountability;
        delete actualUser.accountability;
      }
      return actualUser;
    } catch (_) {}
  }
);

register(
  {
    name: "coachOrganizationInformation",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: [
            "athletes",
            "avatar",
            "name",
            "startingCalendarDay",
            "accountabilityTemplates",
            "questionnaireTemplates",
            "annualPhasesTemplates",
            "athletesGroups",
          ],
        },
      });
      const data = await res.json();
      const { actualUser = {} } = data;
      return actualUser;
    } catch (_) {}
  }
);

/**
 * Which of the coach's own athletes already have an active team/flexible
 * program - `cycle-teams-check-members` already computes this (it was built
 * for the team invite flow's own "already on a team" warning). Used by
 * SendModal to keep a coach from pushing a template/block onto a client
 * whose calendar a program already governs.
 */
register(
  {
    name: "clientsOnProgram",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const athleteIds = (locals.activeUser?.athletes || []).map((athlete) =>
      String(athlete._id)
    );
    if (!athleteIds.length) return [];

    try {
      const res = await api("cycle-teams-check-members", {
        method: "POST",
        body: { recipientIds: athleteIds },
      });
      const { inTeam = [], onFlexibleProgram = [] } = await res.json();
      return [
        ...inTeam.map((entry) => ({ userId: entry.userId, kind: "team" })),
        ...onFlexibleProgram.map((entry) => ({
          userId: entry.userId,
          kind: "flexible",
        })),
      ];
    } catch (_) {
      return [];
    }
  }
);

register(
  {
    name: "allAthletesFromOrganization",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api, locals }) => {
    const organizationId = locals.activeUser.organizationId;
    if (!organizationId) return;
    try {
      const res = await api(`athletes-request-organizations`);
      const data = await res.json();
      const { organizationAthletes = [] } = data;
      return organizationAthletes;
    } catch (_) {}
  }
);

register(
  {
    name: "activeUser",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      return await (await api(`user/${user}`)).json();
    } catch (_) {}
  }
);

register(
  {
    name: "invoice",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      return await (
        await api(`user-invoice`, {
          query: { userId: user },
        })
      ).json();
    } catch (_) {}
  }
);

register(
  {
    name: "userByEmail",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { email }) => {
    if (!email) return;
    try {
      const response = await (
        await api(`user-by-email`, { query: { email } })
      ).json();
      const { user } = response;
      return user;
    } catch (_) {}
  }
);

register(
  {
    name: "profileUser",
    // see the "athlete" loader above for why "exercise" is included
    meta: ["no-bust", "user", "exercise"],
    ttl: "2 hours",
  },
  async ({ api, momentTZ }, { user }) => {
    if (!user) return;
    try {
      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: [
            "country",
            "admin",
            "permissions",
            "ambassador",
            "stripe",
            "exerciseLibrary",
            "repMaximums",
            "primaryExercises",
            "email",
          ],
        },
      });
      const data = await res.json();
      const { actualUser: profileUser = {} } = data;
      return appendTypeAndCountry(profileUser, momentTZ);
    } catch (_) {}
  }
);

register(
  {
    name: "coaches",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async (
    { api },
    {
      name,
      email,
      trialStatus,
      sortBy = "name",
      sortType = "asc",
      onlyPush = false,
    }
  ) => {
    const query = {
      name,
      email,
      limit: false,
      sort: { name: 1 },
      onlyPush,
    };
    const res = await api("coaches", {
      query,
    });
    const resJSON = await res.json();

    let coaches = resJSON.coaches
      .map((coach) => {
        coach.trialStatus = getCoachTrialStatus(coach);
        coach.stripePlan = getCoachStripePlan(coach);
        const daysLeft = calculateFreeTrialDaysLeft(coach);
        coach.daysLeft = daysLeft > 0 ? daysLeft : 0;
        coach.daysSince = daysLeft < 0 ? Math.abs(daysLeft) : 0;
        return coach;
      })
      .filter((coach) => {
        if (trialStatus && trialStatus !== "any") {
          return coach.trialStatus === trialStatus;
        }
        return true;
      });

    coaches = orderBy(coaches, [sortBy], [sortType]);

    let totalAthletesActive = 0,
      totalAthletesInactive = 0,
      totalAthletes = 0,
      totalSubscribers = 0;

    coaches.forEach((coach) => {
      if (onlyPush) {
        totalSubscribers += parseInt(
          coach.subscribers ? coach.subscribers.length : 0,
          10
        );
        return;
      }
      totalAthletes += parseInt(coach.totalAthletes, 10);
      totalAthletesActive += parseInt(coach.active, 10);
      totalAthletesInactive += parseInt(coach.inactive, 10);
    });

    return onlyPush
      ? { coaches, totalSubscribers }
      : {
          coaches,
          totalAthletes,
          totalAthletesActive,
          totalAthletesInactive,
        };
  }
);

register(
  {
    name: "admin_coaches",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { name, email }) => {
    const query = {
      permissions: "coach",
      limit: false,
      sort: { name: 1 },
    };
    if (name) {
      query.name = { $regex: `^.*${name}.*$`, $options: "i" };
    }
    if (email) {
      query.email = { $regex: `^.*${email}.*$`, $options: "i" };
    }
    const res = await api("user", {
      query,
    });
    const resJSON = await res.json();
    return await resJSON.filter(
      (user) => !user.permissions?.includes("coachCanSubmit")
    );
  }
);

register(
  {
    name: "feBranding",
    meta: ["feBranding"],
  },
  async ({ api }) => {
    const res = await api("fe-branding", {});
    const resJSON = await res.json();

    return resJSON;
  }
);
register(
  {
    name: "incomeSync",
    // ttl: 0 — Receptacle expires a record lazily on its next read, so this
    // never serves a session-cached answer. The Income page reads this on
    // every navigation to decide whether a backfill is still running and to
    // show "synced X ago" — a stale answer here is exactly the "confident
    // stale number" requirements §2.1 says to avoid, so it always re-checks.
    ttl: 0,
  },
  async ({ api }, params) => {
    const query = params?.organizationUser
      ? { organizationUser: params.organizationUser }
      : {};
    const res = await api(`fe-income-sync`, { query });
    const resJSON = await res.json();
    return resJSON;
  }
);
register(
  {
    name: "incomeLanding",
    // Same bust keys as feIncomes: lib/stripe-sync/ (msb-api) bumps these on
    // every backfill, incremental sync and webhook ingest, so the landing's
    // headline figures can't outlive a ledger write.
    meta: ["no-bust", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async ({ api }, params) => {
    const query = params?.organizationUser
      ? { organizationUser: params.organizationUser }
      : {};
    const res = await api(`fe-income-landing`, { query });
    const resJSON = await res.json();
    return resJSON;
  }
);
register(
  {
    name: "feIncomes",
    // "stripe-object"/"stripe-subscription-interval" are bumped by
    // lib/stripe-sync/ (msb-api) on every backfill, incremental sync and
    // webhook ingest — that's what busts this before the 2h ttl does, so
    // the ttl is a safety-net ceiling, not the thing keeping this fresh.
    meta: ["no-bust", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async ({ api }, params) => {
    const selector = params.selector || 3;
    let query = {
      selector,
      context: params.context || "all",
    };
    if (params?.organizationUser) {
      query = {
        ...query,
        organizationUser: params.organizationUser,
      };
    }
    const res = await api(`fe-incomes`, {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "planPerformance",
    // "stripe-object"/"stripe-subscription-interval" are bumped by
    // lib/stripe-sync/ (msb-api) on every backfill, incremental sync and
    // webhook ingest — that's what busts this before the 2h ttl does, so
    // the ttl is a safety-net ceiling, not the thing keeping this fresh.
    meta: ["no-bust", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async ({ api }, params) => {
    const selector = params.selector || 3;
    let query = {
      selector,
      context: params.context || "all",
    };
    if (params?.organizationUser) {
      query = {
        ...query,
        organizationUser: params.organizationUser,
      };
    }
    const res = await api(`fe-plan-performance`, {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "failedPayments",
    // "stripe-object"/"stripe-subscription-interval" are bumped by
    // lib/stripe-sync/ (msb-api) on every backfill, incremental sync and
    // webhook ingest — that's what busts this before the 2h ttl does, so
    // the ttl is a safety-net ceiling, not the thing keeping this fresh.
    meta: ["no-bust", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async ({ api }, params) => {
    const query = params?.organizationUser
      ? {
          organizationUser: params.organizationUser,
          context: params.context || "all",
        }
      : { context: params?.context || "all" };
    const res = await api(`fe-failed-payments`, {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "growth",
    // "stripe-object"/"stripe-subscription-interval" are bumped by
    // lib/stripe-sync/ (msb-api) on every backfill, incremental sync and
    // webhook ingest — that's what busts this before the 2h ttl does, so
    // the ttl is a safety-net ceiling, not the thing keeping this fresh.
    meta: ["no-bust", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async ({ api }, params) => {
    const { selector = 3, organizationUser, context = "push" } = params || {};
    const query = organizationUser
      ? { organizationUser, selector, context }
      : { selector, context };
    const res = await api(`fe-growth`, {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "transactions",
    // "stripe-object"/"stripe-subscription-interval" are bumped by
    // lib/stripe-sync/ (msb-api) on every backfill, incremental sync and
    // webhook ingest — that's what busts this before the 2h ttl does, so
    // the ttl is a safety-net ceiling, not the thing keeping this fresh.
    meta: ["no-bust", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async ({ api }, params) => {
    const { selector = 3, product, planId, context } = params || {};
    // The whole window comes back in one page: type/search filtering and
    // sorting happen client-side in TransactionsTable, so a filter change
    // must not cost a round-trip.
    const query = { selector, context: context || "all", limit: 1000 };
    if (product) query.product = product;
    if (planId) query.planId = planId;
    if (params?.organizationUser) {
      query.organizationUser = params.organizationUser;
    }
    const res = await api(`fe-transactions`, {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "payouts",
    // Same bust keys as feIncomes: lib/stripe-sync/ (msb-api) bumps these on
    // every backfill, incremental sync and webhook ingest, so payout and
    // reconciliation figures can't outlive a ledger write.
    meta: ["no-bust", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async ({ api }, params) => {
    // Whole window in one page: status filtering and sorting happen
    // client-side in PayoutsTable, so neither costs a round-trip.
    let query = {
      selector: params.selector || 3,
      context: params.context || "all",
      limit: 1000,
    };
    if (params?.startingAfter) {
      query = { ...query, startingAfter: params.startingAfter };
    }
    if (params?.organizationUser) {
      query = { ...query, organizationUser: params.organizationUser };
    }
    const res = await api(`fe-payouts`, { query });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "coachingBilling",
    meta: ["coachingBilling"],
  },
  async ({ api }, month, year) => {
    const res = await api("coaching-billing", {
      query: {
        month,
        year,
      },
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "performBillingSummary",
    meta: ["performBillingSummary"],
  },
  async ({ api }, periodStart, periodEnd) => {
    const res = await api("perform-get-billing-summary", {
      query: {
        periodStart,
        periodEnd,
      },
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "performMembersList",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }) => {
    const res = await api("perform-members-list");
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "subscribersList",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { startingAfter = null }) => {
    const res = await api("stripe-connected-account-subs", {
      query: {
        startingAfter,
      },
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "stripeRecentEvents",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { customerId = null }) => {
    const res = await api("stripe-recent-activity", {
      query: {
        customerId: customerId,
      },
    });
    const { customerEvents } = await res.json();
    return customerEvents;
  }
);

register(
  {
    name: "subscribersListExport",
    // Reads the Stripe ledger (msb-api getIncomeDataset): without these a
    // coach's first open — before their read-triggered backfill finishes —
    // cached a partial list for the full ttl (sc-46216).
    meta: ["no-bust", "user", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async ({ api }, { memberships = false, organizationUser = undefined }) => {
    let query = { memberships };
    if (organizationUser) {
      query = { memberships, organizationUser };
    }
    const res = await api("subscribers-list-export", {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "activeCommunityChatClientsList",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { organizationUser = undefined }) => {
    let query = {};
    if (organizationUser) {
      query = { organizationUser };
    }
    const res = await api("active-subscribers-members-list", {
      query,
    });
    const resJSON = await res.json();
    const data = resJSON?.allCommunityUsers || [];
    return data;
  }
);

register(
  {
    name: "subscriberInvoices",
    // Reads the Stripe ledger (msb-api getIncomeDataset) — same bust keys as
    // the income loaders, or a backfill finishing never reaches this cache.
    meta: ["no-bust", "stripe-object", "stripe-subscription-interval"],
    ttl: "2 hours",
  },
  async (
    { api },
    { customer, product, productType, organizationUser = undefined }
  ) => {
    let query = { customer, product, productType };
    if (organizationUser) {
      query = { customer, product, productType, organizationUser };
    }
    const res = await api("subscriber-invoices", {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "upcomingInvoice",
    meta: ["no-bust"],
    ttl: "2 hours",
  },
  async ({ api }, { customer, price }) => {
    const res = await api("retrieve-upcoming-invoice", {
      query: { proxyUser: customer, price },
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "pendingAthleteInvitations",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      const createInvitedUser = (email, arr) =>
        arr.filter((element) => element.email === email)[0] || {
          email: email,
          notRegistered: true,
        };

      const res = await api("user-info", {
        query: {
          _id: user,
          customFields: ["pendingAthleteInvitations"],
        },
      });
      const data = await res.json();
      const { actualUser = {} } = data;

      const invitedEmails = actualUser.pendingAthleteInvitations || [];

      // avoid making queries with empty arrays because in some edge cases it pulls the entire collection causing slow load times
      if (!invitedEmails || !invitedEmails.length) return [];

      const invitedRegisteredUsers =
        (await (
          await api("user", {
            query: {
              limit: false,
              email: { $in: invitedEmails },
              fields: ["name", "email", "avatar"],
            },
          })
        ).json()) || [];

      const invitedUsers = invitedEmails.map((invitedEmail) => {
        const user = createInvitedUser(invitedEmail, invitedRegisteredUsers);
        user.invited = true;
        return user;
      });

      return invitedUsers;
    } catch (_) {}
  }
);

register(
  {
    name: "coachingDashboardForMonth",
    meta: [
      "no-bust",
      "user",
      "exercise",
      "daily-stats",
      "coach-notes",
      "video-record",
      "comment",
      "coach-chat-activity",
    ],
    ttl: "2 hours",
  },
  async ({ api }, month, year, organizationCoach, metric) => {
    let query = {
      month,
      year,
    };
    if (organizationCoach) {
      query = { ...query, organizationCoach };
    }
    if (metric) {
      query = { ...query, metric };
    }
    const res = await api("coaching-dashboard-for-month", {
      query,
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

register(
  {
    name: "coach",
    meta: ["no-bust", "user"],
    ttl: "2 hours",
  },
  async ({ api }, { user }) => {
    if (!user) return;
    try {
      return await (
        await api(`user/${user}`, {
          query: {
            fields: [
              "permissions",
              "coachingFreeTrial",
              "accountabilityTemplates",
            ],
          },
        })
      ).json();
    } catch (_) {}
  }
);

register(
  {
    name: "coachingReceipts",
    meta: ["coachingReceipts"],
  },
  async ({ api }, startDateUnix, endDateUnix) => {
    const res = await api("stripe-invoice-bill", {
      query: {
        startDateUnix,
        endDateUnix,
        product: "pull",
      },
    });
    const resJSON = await res.json();

    return resJSON;
  }
);

register(
  {
    name: "performReceipts",
    meta: ["performReceipts"],
  },
  async ({ api }, startDateUnix, endDateUnix) => {
    const res = await api("stripe-invoice-bill", {
      query: {
        startDateUnix,
        endDateUnix,
        product: "perform",
      },
    });
    const resJSON = await res.json();

    return resJSON;
  }
);

register(
  {
    name: "pendingInvoices",
    meta: ["pendingInvoices"],
  },
  async ({ api, locals }) => {
    const user = locals.activeUserToRequest;
    const res = await api("unpaid-invoices", {
      query: {
        user: locals.activeUserToRequest._id,
        subscription:
          user.coachingBilling?.subscriptionId ??
          user.coachingBilling?.subscription,
      },
    });
    const resJSON = await res.json();
    return resJSON.invoices;
  }
);

register(
  {
    name: "pendingPerformInvoices",
    meta: ["pendingPerformInvoices"],
  },
  async ({ api, locals }) => {
    const user = locals.activeUserToRequest;
    const res = await api("unpaid-invoices", {
      query: {
        user: user._id,
        subscription: user.performBilling?.subscription,
      },
    });
    const resJSON = await res.json();
    return resJSON.invoices;
  }
);

register(
  {
    name: "teamsOverview",
    meta: ["user", "exercise"],
    ttl: "2 hours",
  },
  async ({ api }, month, year, teamId) => {
    const query = {
      month,
      year,
      teamId,
    };

    const res = await api("teams-overview", {
      query,
    });

    const resJSON = await res.json();

    return resJSON;
  }
);

register(
  {
    name: "challengeReport",
    meta: ["no-bust", "user", "challenge-report-templates"],
    ttl: "2 hours",
  },
  async ({ api }, { dateFrom, dateTo, type = "push" }) => {
    const res = await api("challenge-report", {
      query: { dateFrom, dateTo, type },
    });
    const resJSON = await res.json();
    return resJSON;
  }
);

// Member leaderboards published from the coach Leaderboard page.
register(
  {
    name: "leaderboardBoards",
    meta: ["no-bust", "challenge-report-templates"],
    ttl: "2 hours",
  },
  async ({ api }, { audience, programId } = {}) => {
    const res = await api("leaderboard-boards", {
      query: {
        ...(audience && { audience }),
        ...(programId && { programId }),
      },
    });
    return res.json();
  }
);

register(
  {
    name: "leaderboardPreview",
    meta: ["no-bust", "challenge-report-templates"],
    ttl: "30 minutes",
  },
  async ({ api }, { templateId, audience, programId }) => {
    const res = await api("leaderboard-preview", {
      query: { templateId, audience, ...(programId && { programId }) },
    });
    return res.json();
  }
);

register(
  {
    name: "challengeReportTemplates",
    meta: ["no-bust", "challenge-report-templates"],
    ttl: "2 hours",
  },
  async ({ api }) => {
    try {
      const res = await api("challenge-report-templates");
      if (!res.ok) return [];
      const text = await res.text();
      if (!text || text.trim() === "") return [];
      const data = JSON.parse(text);
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.templates)) return data.templates;
      return [];
    } catch {
      return [];
    }
  }
);

register(
  {
    name: "nutritionTableTemplates",
    meta: ["no-bust", "nutrition-table-templates"],
    ttl: "2 hours",
  },
  async ({ api }) => {
    try {
      const res = await api("nutrition-table-templates");
      if (!res.ok) return [];
      const text = await res.text();
      if (!text || text.trim() === "") return [];
      const data = JSON.parse(text);
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.templates)) return data.templates;
      return [];
    } catch {
      return [];
    }
  }
);
